// Centralized error handling utilities

export interface AppError extends Error {
  code?: string;
  statusCode?: number;
  context?: Record<string, any>;
}

export class ValidationError extends Error implements AppError {
  code = 'VALIDATION_ERROR';
  statusCode = 400;
  context?: Record<string, any>;

  constructor(message: string, context?: Record<string, any>) {
    super(message);
    this.name = 'ValidationError';
    this.context = context;
  }
}

export class PaymentError extends Error implements AppError {
  code = 'PAYMENT_ERROR';
  statusCode = 400;
  context?: Record<string, any>;

  constructor(message: string, context?: Record<string, any>) {
    super(message);
    this.name = 'PaymentError';
    this.context = context;
  }
}

export class NetworkError extends Error implements AppError {
  code = 'NETWORK_ERROR';
  statusCode = 500;
  context?: Record<string, any>;

  constructor(message: string, context?: Record<string, any>) {
    super(message);
    this.name = 'NetworkError';
    this.context = context;
  }
}

// Error categorization for user-friendly messages
export const getErrorCategory = (error: Error): string => {
  if (error.message.includes('network') || error.message.includes('fetch')) {
    return 'Network connection issue. Please check your internet connection.';
  }
  
  if (error.message.includes('email')) {
    return 'Please check your email address and try again.';
  }
  
  if (error.message.includes('password')) {
    return 'Please check your password and try again.';
  }
  
  if (error.message.includes('payment')) {
    return 'Payment processing failed. Please try again or use a different payment method.';
  }
  
  return error.message;
};

// Safe error logging (removes sensitive information)
export const logError = (error: Error, context?: Record<string, any>) => {
  const sanitizedContext = { ...context };
  
  // Remove sensitive fields
  delete sanitizedContext.password;
  delete sanitizedContext.email;
  delete sanitizedContext.token;
  
  if (import.meta.env.DEV) {
    console.error('Application Error:', {
      message: error.message,
      stack: error.stack,
      context: sanitizedContext
    });
  }
  
  // In production, you would send this to your error tracking service
  // e.g., Sentry, LogRocket, etc.
};